import React, { useState } from 'react';
import { BarChart3Icon, DownloadIcon, TrendingUpIcon, UsersIcon, ShieldCheckIcon, CalendarIcon } from 'lucide-react';
import { formatNaira } from '../../utils/format';
export function ReportingModule({
  role
}: {
  role: string;
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const tabs = [{
    id: 'overview',
    label: 'Platform Overview'
  }, {
    id: 'sales',
    label: 'Policy Sales'
  }, {
    id: 'premium',
    label: 'Premium Collection'
  }, {
    id: 'claims',
    label: 'Claims Report'
  }];
  if (role === 'Super Admin') {
    tabs.push({
      id: 'reconciliation',
      label: 'Underwriter Reconciliation'
    }, {
      id: 'wallet',
      label: 'Wallet Balance'
    }, {
      id: 'commission',
      label: 'Commission Tracking'
    });
  }
  return <div className="space-y-6" data-id="element-699">
      <div className="flex justify-between items-center" data-id="element-700">
        <div data-id="element-701">
          <h2 className="text-2xl font-bold text-[#0D3B3B]" data-id="element-702">
            Reporting Dashboard
          </h2>
          <p className="text-sm text-gray-500" data-id="element-703">
            Real-time visibility into platform performance.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-704">
          <div className="relative" data-id="element-705">
            <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-706" />
            <select className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 appearance-none" data-id="element-707">
              <option data-id="element-708">This Month</option>
              <option data-id="element-709">Last Month</option>
              <option data-id="element-710">This Quarter</option>
              <option data-id="element-711">Year to Date</option>
            </select>
          </div>
          <button className="bg-[#0D3B3B] text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-[#092D2D] transition-colors" data-id="element-712">
            <DownloadIcon className="w-4 h-4" data-id="element-713" /> Export Report
          </button>
        </div>
      </div>

      {/* Report Tabs */}
      <div className="flex overflow-x-auto scrollbar-hide gap-2 pb-2" data-id="element-714">
        {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-[#2BA89D] text-white shadow-md shadow-teal-900/10' : 'bg-white text-gray-500 hover:bg-gray-50 border border-gray-200'}`} data-id="element-715">
            {tab.label}
          </button>)}
      </div>

      {/* Content Area (Simulated visual representation) */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 min-h-[400px]" data-id="element-716">
        <div className="flex items-center gap-3 mb-8" data-id="element-717">
          <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center text-teal-600" data-id="element-718">
            <BarChart3Icon className="w-6 h-6" data-id="element-719" />
          </div>
          <div data-id="element-720">
            <h3 className="font-bold text-[#0D3B3B] text-xl" data-id="element-721">
              {tabs.find(t => t.id === activeTab)?.label}
            </h3>
            <p className="text-sm text-gray-500" data-id="element-722">March 1 - March 30, 2026</p>
          </div>
        </div>

        {activeTab === 'overview' && <div className="space-y-8" data-id="element-723">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-724">
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100" data-id="element-725">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-726">
                  Total Revenue (MTD)
                </p>
                <h4 className="text-3xl font-black text-[#0D3B3B]" data-id="element-727">
                  {formatNaira(45200000)}
                </h4>
                <p className="text-sm text-emerald-600 font-bold mt-2 flex items-center gap-1" data-id="element-728">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-729" /> +8.5% vs last month
                </p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100" data-id="element-730">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-731">
                  Active Plans
                </p>
                <h4 className="text-3xl font-black text-[#0D3B3B]" data-id="element-732">12,450</h4>
                <p className="text-sm text-emerald-600 font-bold mt-2 flex items-center gap-1" data-id="element-733">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-734" /> +12% vs last month
                </p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100" data-id="element-735">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-736">
                  New Users
                </p>
                <h4 className="text-3xl font-black text-[#0D3B3B]" data-id="element-737">1,205</h4>
                <p className="text-sm text-emerald-600 font-bold mt-2 flex items-center gap-1" data-id="element-738">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-739" /> +5% vs last month
                </p>
              </div>
            </div>

            <div data-id="element-740">
              <h4 className="font-bold text-[#0D3B3B] mb-4" data-id="element-741">
                Revenue by Product
              </h4>
              <div className="space-y-4" data-id="element-742">
                {[{
              name: 'AutoGuard',
              value: 45,
              color: 'bg-indigo-500'
            }, {
              name: 'Future Scholar Fund',
              value: 30,
              color: 'bg-emerald-500'
            }, {
              name: 'Wellness Plan',
              value: 15,
              color: 'bg-cyan-500'
            }, {
              name: 'Others',
              value: 10,
              color: 'bg-gray-400'
            }].map((item, idx) => <div key={idx} data-id="element-743">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-744">
                      <span className="font-bold text-gray-700" data-id="element-745">
                        {item.name}
                      </span>
                      <span className="font-bold text-[#0D3B3B]" data-id="element-746">
                        {item.value}%
                      </span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-747">
                      <div className={`h-full ${item.color} rounded-full`} style={{
                  width: `${item.value}%`
                }} data-id="element-748"></div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}

        {activeTab !== 'overview' && <div className="flex flex-col items-center justify-center h-64 text-center" data-id="element-749">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-400 mb-4" data-id="element-750">
              <BarChart3Icon className="w-8 h-8" data-id="element-751" />
            </div>
            <h4 className="text-lg font-bold text-[#0D3B3B] mb-2" data-id="element-752">
              Report Data Available
            </h4>
            <p className="text-sm text-gray-500 max-w-sm" data-id="element-753">
              The full {tabs.find(t => t.id === activeTab)?.label} dataset is
              available for export. Click the Export Report button to download
              the CSV.
            </p>
          </div>}
      </div>
    </div>;
}